import { useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link2,
  Unlink,
  Heading,
  Eraser,
  Code2,
  X,
} from "lucide-react";
import { isFullHtmlDocument } from "../htmlBody";

/**
 * A small HTML composer for the mail-out body.
 *
 * Deliberately dependency-free: the portal ships no rich-text library, and an
 * email body needs bold, lists and links rather than a full editor. The value
 * is written into the node only when it differs from what this editor last
 * emitted — a contentEditable re-rendered from its own output puts the caret
 * back at the start on every keystroke.
 *
 * The </> button opens the source window, where a mailer designed elsewhere can
 * be pasted in whole. A pasted DOCUMENT is not fed to the contentEditable at
 * all — the browser would rewrite its table layout on the first keystroke, and
 * its <style> block would escape into the admin portal's own page. It is held
 * verbatim and shown in a sandboxed frame instead, which is also a truer
 * preview of the mail than the composer could give.
 *
 * The HTML is sanitised server-side before it is stored or sent.
 */
type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
};

export default function RichTextEditor({ value, onChange, placeholder, disabled }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef<string>("");

  const [sourceOpen, setSourceOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const raw = isFullHtmlDocument(value);

  useEffect(() => {
    const node = ref.current;
    // Nothing to sync while the document preview is standing in for the editor.
    if (!node || raw) return;
    if ((value || "") !== lastEmitted.current) {
      node.innerHTML = value || "";
      lastEmitted.current = value || "";
    }
  }, [value, raw]);

  const emit = () => {
    const html = ref.current?.innerHTML ?? "";
    lastEmitted.current = html;
    onChange(html);
  };

  const exec = (command: string, arg?: string) => {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link address (include https://)");
    if (!url) return;
    exec("createLink", url);
  };

  const openSource = () => {
    if (disabled) return;
    setDraft(value || "");
    setSourceOpen(true);
  };

  const applySource = () => {
    const html = draft.trim();
    // The contentEditable's cached copy is now stale; clearing it lets the sync
    // effect write a pasted snippet back into the node.
    lastEmitted.current = "";
    onChange(html);
    setSourceOpen(false);
  };

  const isEmpty = !value || value.replace(/<[^>]*>/g, "").trim() === "";

  const Tool = ({
    icon,
    label,
    onClick,
    active,
  }: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    active?: boolean;
  }) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      // Keeps the selection alive — a focused button would collapse it before
      // execCommand ever runs.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      className={`p-2 rounded-lg disabled:opacity-40 transition-all ${
        active
          ? "bg-gray-900 text-white shadow-sm"
          : "text-gray-500 hover:bg-white hover:text-gray-900 hover:shadow-sm"
      }`}
    >
      {icon}
    </button>
  );

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      {/* Tailwind's reset strips list markers and heading sizes, which would
          make the composer lie about how the email will look. */}
      <style>{`
        .prose-mail h3 { font-size: 1.05rem; font-weight: 700; margin: 1rem 0 .5rem; }
        .prose-mail ul { list-style: disc; padding-left: 1.5rem; margin: .5rem 0; }
        .prose-mail ol { list-style: decimal; padding-left: 1.5rem; margin: .5rem 0; }
        .prose-mail li { margin: .2rem 0; }
        .prose-mail a { color: #0b2e6b; text-decoration: underline; }
        .prose-mail p { margin: 0 0 .75rem; }
      `}</style>

      <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 bg-gray-50 border-b border-gray-100">
        {!raw && (
          <>
            <Tool icon={<Bold className="w-4 h-4" />} label="Bold" onClick={() => exec("bold")} />
            <Tool icon={<Italic className="w-4 h-4" />} label="Italic" onClick={() => exec("italic")} />
            <Tool
              icon={<Underline className="w-4 h-4" />}
              label="Underline"
              onClick={() => exec("underline")}
            />
            <span className="w-px h-5 bg-gray-200 mx-1.5" />
            <Tool
              icon={<Heading className="w-4 h-4" />}
              label="Heading"
              onClick={() => exec("formatBlock", "<h3>")}
            />
            <Tool
              icon={<List className="w-4 h-4" />}
              label="Bulleted list"
              onClick={() => exec("insertUnorderedList")}
            />
            <Tool
              icon={<ListOrdered className="w-4 h-4" />}
              label="Numbered list"
              onClick={() => exec("insertOrderedList")}
            />
            <span className="w-px h-5 bg-gray-200 mx-1.5" />
            <Tool icon={<Link2 className="w-4 h-4" />} label="Insert link" onClick={addLink} />
            <Tool
              icon={<Unlink className="w-4 h-4" />}
              label="Remove link"
              onClick={() => exec("unlink")}
            />
            <Tool
              icon={<Eraser className="w-4 h-4" />}
              label="Clear formatting"
              onClick={() => exec("removeFormat")}
            />
            <span className="w-px h-5 bg-gray-200 mx-1.5" />
          </>
        )}

        <Tool
          icon={<Code2 className="w-4 h-4" />}
          label="Source code — paste a ready-made HTML mailer"
          onClick={openSource}
          active={raw}
        />

        {raw && (
          <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">
            HTML document — preview below
          </span>
        )}
      </div>

      {raw ? (
        <div className="relative">
          {/* No allow-scripts: the paste renders, it does not run. */}
          <iframe
            title="Message preview"
            srcDoc={value}
            sandbox=""
            className="w-full h-[560px] bg-white border-0"
          />
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[10px] text-gray-400">
              Pasted HTML is sent as the whole message — the Institute letterhead is not added
              around it. Put <code className="font-bold text-gray-500">{"{{name}}"}</code> wherever
              the recipient's name belongs.
            </p>
            <button
              type="button"
              onClick={openSource}
              disabled={disabled}
              className="shrink-0 inline-flex items-center gap-2 border bg-white rounded-lg px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <Code2 className="w-3.5 h-3.5" /> Edit source
            </button>
          </div>
        </div>
      ) : (
        <div className="relative">
          {isEmpty && placeholder && (
            <span className="absolute top-4 left-4 text-sm text-gray-300 pointer-events-none">
              {placeholder}
            </span>
          )}
          <div
            ref={ref}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={emit}
            onBlur={emit}
            className="min-h-[280px] max-h-[560px] overflow-y-auto p-4 text-sm leading-7 text-gray-800 outline-none prose-mail"
          />
        </div>
      )}

      {sourceOpen && (
        <div className="fixed inset-0 z-50 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-black uppercase tracking-[0.15em] text-gray-900">
                  Source code
                </h3>
                <p className="text-[11px] text-gray-400 mt-1">
                  Paste the contents of your .html file here, then Save.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSourceOpen(false)}
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-50"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              autoFocus
              placeholder={"<!DOCTYPE html>\n<html>\n  …"}
              className="flex-1 min-h-[45vh] m-6 mt-4 border border-gray-200 rounded-2xl p-4 font-mono text-xs leading-5 text-gray-800 outline-none focus:border-gray-900 resize-none"
            />

            <div className="px-6 pb-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setSourceOpen(false)}
                className="border bg-white rounded-xl px-5 py-3 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applySource}
                className="bg-gray-900 text-white rounded-xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-primary active:scale-95 transition-all"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
